import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Bus, ClipboardList, LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { clearAuth, getUserName } from '../lib/auth';
import { toast } from 'sonner';

const COMPANY_LOGO = 'https://customer-assets.emergentagent.com/job_coachcheck/artifacts/w12hb0ce_Screenshot_20260118_231725_Samsung%20Internet.jpg';

const NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/drivers', label: 'Drivers', icon: Users },
  { path: '/admin/vehicles', label: 'Vehicles', icon: Bus },
  { path: '/admin/checks', label: 'Checks', icon: ClipboardList },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userName = getUserName();

  const handleLogout = () => {
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="lg:hidden bg-blue-900 text-white py-3 px-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src={COMPANY_LOGO} 
              alt="City Cabs" 
              className="h-10 w-10 rounded-lg object-cover bg-white"
            />
            <div>
              <h1 className="font-bold text-base leading-tight">City Cabs</h1>
              <p className="text-blue-200 text-xs">Admin Portal</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white hover:bg-blue-800"
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X /> : <Menu />}
          </Button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full w-64 bg-blue-900 sidebar-texture z-50
          transform transition-transform duration-200 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        data-testid="admin-sidebar"
      >
        {/* Sidebar Header */}
        <div className="p-6 border-b border-blue-800">
          <div className="flex items-center gap-3">
            <img 
              src={COMPANY_LOGO} 
              alt="City Cabs" 
              className="h-12 w-12 rounded-lg object-cover bg-white"
            />
            <div>
              <h1 className="font-bold text-white">City Cabs</h1>
              <p className="text-blue-300 text-sm">Admin Portal</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2">
          {NAV_ITEMS.map(({ path, label, icon: Icon }) => (
            <button
              key={path}
              onClick={() => { navigate(path); setSidebarOpen(false); }}
              className={`
                w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium
                transition-colors duration-150
                ${isActive(path) 
                  ? 'bg-yellow-500 text-blue-950' 
                  : 'text-blue-100 hover:bg-blue-800'
                }
              `}
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              {label}
            </button>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-800">
          <div className="text-blue-300 text-sm mb-3 px-2">
            Signed in as <span className="font-medium text-white">{userName}</span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-blue-100 hover:bg-blue-800 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
